import { z } from 'zod';

// Generic Jira wire contracts — shared by every domain that can post to Jira
// (apps/incidents, apps/support). Nothing here knows about an incident or a
// support ticket: `id` is whichever entity the calling router owns, and the
// option/search shapes are Jira's, not a domain's.
//
// The runtime half of this integration lives in apps/api/src/shared/jira.
// Keeping the contracts here too means there is ONE definition of what a Jira
// posting form asks for and returns, so the shared PostToJiraModal can be typed
// against it directly.

// An attachment shown in the modal and uploaded to the issue after creation.
// Structurally identical across domains (uploadthing's DTO), so it is declared
// once rather than per domain.
export const jiraAttachmentSchema = z.object({
  key: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number().optional(),
  contentType: z.string().optional(),
});
export type JiraAttachment = z.infer<typeof jiraAttachmentSchema>;

// getJiraCreateOptions feeds the modal with safe defaults + allowed options
// (never secrets); postToJira accepts the operator's confirmed/adjusted values
// (all optional → the service falls back to the same computed defaults).
export const postToJiraInput = z.object({
  id: z.uuid(),
  project: z.string().min(1).max(50).optional(),
  issueType: z.string().min(1).max(100).optional(),
  priority: z.string().max(100).nullable().optional(),
  summary: z.string().min(1).max(255).optional(),
  parentKey: z.string().max(100).nullable().optional(),
  reporterAccountId: z.string().max(200).nullable().optional(),
  // Display name of the selected Jira reporter — preserved in the description
  // when Jira won't accept the reporter field.
  reporterName: z.string().max(300).nullable().optional(),
  orgReportedBy: z.string().max(300).nullable().optional(),
  orgAffected: z.string().max(300).nullable().optional(),
});
export type PostToJiraInput = z.infer<typeof postToJiraInput>;

export const refreshJiraStatusInput = z.object({ id: z.uuid() });
export type RefreshJiraStatusInput = z.infer<typeof refreshJiraStatusInput>;

// A LIVE read of the linked issue's current workflow status AND its Customer
// Resolution Note, for a domain that shows both without persisting either
// (apps/support). Distinct from
// `refreshJiraStatus`, which is a MUTATION: incidents stores the status on the
// row (`incident.jira_status`) and refreshes it on demand, so its value can be
// listed and filtered but goes stale between refreshes. This contract has no
// stored counterpart — the caller fetches it each time the entity is opened.
//
// `unavailable` is what keeps a Jira outage out of the caller's way: the
// procedure resolves rather than throwing when Jira cannot be reached, so the
// entity still opens and the UI just omits the status. `status` is null both
// when Jira returned no status and when the fetch failed — `unavailable`
// distinguishes the two.
export const getJiraIssueStatusInput = z.object({ id: z.uuid() });
export type GetJiraIssueStatusInput = z.infer<typeof getJiraIssueStatusInput>;

// `customerResolutionNote` is the customer-facing closing wording a Jira agent
// writes on the issue (a per-site custom field — `customfield_10337` on the
// default deployment). Null when the issue has none, when the deployment does
// not configure the field, and whenever the fetch failed — so it is read
// alongside `status` and shown under it, never on its own.
export const jiraIssueStatusSchema = z.object({
  key: z.string(),
  status: z.string().nullable(),
  customerResolutionNote: z.string().nullable(),
  unavailable: z.boolean(),
});
export type JiraIssueStatus = z.infer<typeof jiraIssueStatusSchema>;

export const getJiraCreateOptionsInput = z.object({ id: z.uuid() });
export type GetJiraCreateOptionsInput = z.infer<
  typeof getJiraCreateOptionsInput
>;

// A Jira project/space the token can create issues in (loaded from Jira).
export const jiraProjectOptionSchema = z.object({
  key: z.string(),
  name: z.string(),
});
export type JiraProjectOption = z.infer<typeof jiraProjectOptionSchema>;

// An issue type + its Jira hierarchy level (subtask=-1, standard=0, epic=1, …).
// Drives valid-parent computation without hardcoded mappings: a type at level N
// may only be parented by a type at level N+1.
export const jiraIssueTypeLevelSchema = z.object({
  name: z.string(),
  hierarchyLevel: z.number().int(),
});
export type JiraIssueTypeLevel = z.infer<typeof jiraIssueTypeLevelSchema>;

// Safe modal payload — defaults + allowed options loaded from Jira. No secrets.
export const jiraCreateOptionsSchema = z.object({
  // All Jira projects/spaces available to the token (best-effort; may be empty).
  projects: z.array(jiraProjectOptionSchema),
  // Resolved default project key (kept for back-compat; = defaults.project).
  projectKey: z.string(),
  // Issue/work types for the DEFAULT project (reloaded per-project on change).
  issueTypes: z.array(z.string()),
  // Issue types + hierarchy levels → the modal computes valid Parent types.
  issueTypeHierarchy: z.array(jiraIssueTypeLevelSchema),
  priorities: z.array(z.string()),
  // Organization option pools for the "reported by"/"affected" selects. Sourced
  // from Jira custom-field options when the project exposes them, else Xthory
  // organizations by name (orgFromJira=false).
  orgReportedOptions: z.array(z.string()),
  orgAffectedOptions: z.array(z.string()),
  orgFromJira: z.boolean(),
  // The entity's attachments — shown in the modal before posting; uploaded to
  // the Jira issue after it's created.
  attachments: z.array(jiraAttachmentSchema),
  reporter: z
    .object({ accountId: z.string(), displayName: z.string() })
    .nullable(),
  // Set when Jira metadata couldn't be loaded → the modal uses safe defaults.
  metadataWarning: z.string().nullable(),
  defaults: z.object({
    project: z.string(),
    issueType: z.string(),
    priority: z.string().nullable(),
    summary: z.string(),
    descriptionBase: z.string(),
    parentKey: z.string().nullable(),
    reporterAccountId: z.string().nullable(),
    orgReportedBy: z.string().nullable(),
    orgAffected: z.string().nullable(),
  }),
});
export type JiraCreateOptions = z.infer<typeof jiraCreateOptionsSchema>;

// Per-project metadata — issue types + priorities for a chosen project, fetched
// when the operator switches the project in the modal (Jira create-meta is
// project-scoped). Backend-only Jira calls; frontend gets safe options.
export const getJiraProjectMetaInput = z.object({
  id: z.uuid(),
  project: z.string().min(1).max(50),
});
export type GetJiraProjectMetaInput = z.infer<typeof getJiraProjectMetaInput>;

export const jiraProjectMetaSchema = z.object({
  project: z.string(),
  issueTypes: z.array(z.string()),
  priorities: z.array(z.string()),
  // Issue types + hierarchy levels for THIS project → valid-parent calc.
  issueTypeHierarchy: z.array(jiraIssueTypeLevelSchema),
  // Org option pools for THIS project (reloaded on project switch).
  orgReportedOptions: z.array(z.string()),
  orgAffectedOptions: z.array(z.string()),
  orgFromJira: z.boolean(),
  defaultIssueType: z.string(),
  defaultPriority: z.string().nullable(),
  defaultOrgReportedBy: z.string().nullable(),
  defaultOrgAffected: z.string().nullable(),
  warning: z.string().nullable(),
});
export type JiraProjectMeta = z.infer<typeof jiraProjectMetaSchema>;

// Reporter picker — searchable Jira users (backend-only Jira call).
export const jiraUserSchema = z.object({
  accountId: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
});
export type JiraUser = z.infer<typeof jiraUserSchema>;

export const searchJiraUsersInput = z.object({
  id: z.uuid(),
  query: z.string().max(200),
});
export type SearchJiraUsersInput = z.infer<typeof searchJiraUsersInput>;

export const searchJiraUsersResult = z.array(jiraUserSchema);
export type SearchJiraUsersResult = z.infer<typeof searchJiraUsersResult>;

// Parent picker — a Jira issue (key + id + summary + type/status for the row
// icon + done filtering).
export const jiraIssueRefSchema = z.object({
  key: z.string(),
  id: z.string(),
  summary: z.string(),
  issueType: z.string().nullable(),
  statusCategory: z.string().nullable(),
});
export type JiraIssueRef = z.infer<typeof jiraIssueRefSchema>;

export const searchJiraIssuesInput = z.object({
  id: z.uuid(),
  project: z.string().min(1).max(50),
  query: z.string().max(200),
  // Jira's "Show everything marked as done" — include Done-category issues.
  includeDone: z.boolean().default(false),
  // Valid parent issue-type names for the selected issue type (Jira hierarchy).
  // Omitted → no restriction (hierarchy unknown); [] → no valid parents.
  parentTypes: z.array(z.string()).optional(),
});
export type SearchJiraIssuesInput = z.infer<typeof searchJiraIssuesInput>;

export const searchJiraIssuesResult = z.array(jiraIssueRefSchema);
export type SearchJiraIssuesResult = z.infer<typeof searchJiraIssuesResult>;

// Attachment sync — upload the entity's attachments to its Jira issue after
// the issue is created. Never fails the post; returns counts for a warning.
export const syncJiraAttachmentsInput = z.object({ id: z.uuid() });
export type SyncJiraAttachmentsInput = z.infer<typeof syncJiraAttachmentsInput>;

export const syncJiraAttachmentsResult = z.object({
  uploaded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type SyncJiraAttachmentsResult = z.infer<
  typeof syncJiraAttachmentsResult
>;
